'use client'

import * as React from 'react'
import * as TabsPrimitive from '@radix-ui/react-tabs'
import { cn } from '../cn'

const TabsMotionContext = React.createContext<{
  activeValue: string | undefined
} | null>(null)

function Tabs({
  className,
  value,
  defaultValue,
  onValueChange,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Root>) {
  const [internalValue, setInternalValue] = React.useState(defaultValue)
  const activeValue = value ?? internalValue

  const handleValueChange = React.useCallback(
    (nextValue: string) => {
      setInternalValue(nextValue)
      onValueChange?.(nextValue)
    },
    [onValueChange]
  )

  return (
    <TabsMotionContext.Provider value={{ activeValue }}>
      <TabsPrimitive.Root
        data-slot='tabs'
        className={cn('flex flex-col gap-2', className)}
        value={activeValue}
        defaultValue={defaultValue}
        onValueChange={handleValueChange}
        {...props}
      />
    </TabsMotionContext.Provider>
  )
}

function TabsList({
  className,
  activeClassName: _activeClassName,
  ref,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List> & {
  activeClassName?: string
}) {
  const motionContext = React.useContext(TabsMotionContext)
  const listRef = React.useRef<React.ElementRef<
    typeof TabsPrimitive.List
  > | null>(null)
  const hasMeasuredRef = React.useRef(false)
  const [indicator, setIndicator] = React.useState({
    offset: 0,
    width: 0,
    visible: false,
    animate: false,
  })

  const setListRef = React.useCallback(
    (node: React.ElementRef<typeof TabsPrimitive.List> | null) => {
      listRef.current = node
      if (typeof ref === 'function') ref(node)
      else if (ref) ref.current = node
    },
    [ref]
  )

  const measureIndicator = React.useCallback(() => {
    const list = listRef.current
    const activeTab = list?.querySelector<HTMLElement>(
      '[data-slot="tabs-trigger"][data-state="active"]'
    )
    if (!list || !activeTab) {
      setIndicator((current) => ({ ...current, visible: false }))
      return
    }

    const next = {
      offset: activeTab.offsetLeft + 8,
      width: Math.max(0, activeTab.clientWidth - 16),
      visible: true,
      animate: hasMeasuredRef.current,
    }
    hasMeasuredRef.current = true
    setIndicator((current) =>
      current.offset === next.offset &&
      current.width === next.width &&
      current.visible === next.visible &&
      current.animate === next.animate
        ? current
        : next
    )
  }, [])

  React.useLayoutEffect(() => {
    measureIndicator()
    const list = listRef.current
    if (!list || typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver(measureIndicator)
    observer.observe(list)
    const activeTab = list.querySelector<HTMLElement>(
      '[data-slot="tabs-trigger"][data-state="active"]'
    )
    if (activeTab) observer.observe(activeTab)
    return () => observer.disconnect()
  }, [measureIndicator])

  React.useEffect(() => {
    if (!hasMeasuredRef.current) return
    const frame = requestAnimationFrame(measureIndicator)
    return () => cancelAnimationFrame(frame)
  }, [measureIndicator, motionContext?.activeValue])

  return (
    <TabsPrimitive.List
      ref={setListRef}
      data-slot='tabs-list'
      className={cn(
        'border-border relative inline-flex h-auto w-fit items-center justify-start gap-1 overflow-x-auto overflow-y-hidden border-b bg-transparent p-0',
        className
      )}
      {...props}
    >
      {props.children}
      <span
        aria-hidden='true'
        className={cn(
          'pointer-events-none absolute -bottom-px left-0 h-[3px] rounded-full bg-primary motion-reduce:transition-none',
          indicator.animate
            ? 'transition-transform duration-200 ease-emphasized'
            : 'transition-none',
          indicator.visible ? 'opacity-100' : 'opacity-0'
        )}
        style={{
          width: indicator.width,
          transform: `translateX(${indicator.offset}px)`,
        }}
      />
    </TabsPrimitive.List>
  )
}

function TabsTrigger({
  className,
  children,
  value,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  return (
    <TabsPrimitive.Trigger
      data-slot='tabs-trigger'
      className={cn(
        "text-text-placeholder hover:text-muted-foreground focus-visible:ring-ring data-[state=active]:text-primary relative inline-flex h-9 shrink-0 cursor-pointer items-center justify-center gap-1.5 px-3 text-sm font-medium whitespace-nowrap transition-colors focus-visible:ring-1 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-3.5",
        className
      )}
      value={value}
      {...props}
    >
      {children}
    </TabsPrimitive.Trigger>
  )
}

function TabsContent({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      data-slot='tabs-content'
      className={cn('flex-1 outline-none', className)}
      {...props}
    />
  )
}

export { Tabs, TabsList, TabsTrigger, TabsContent }
